import React from 'react';
import * as yup from 'yup';
import { Box } from '@mui/material';
import {
  SparkForm,
  SparkFormButton,
  SparkFormResetButton,
} from 'sharedComponents/SparkForm';
import MemoryThumbnail from './ImagesAndMedia/MemoryThumbnail';
import MemoryBasicDetails from './MainInfo/MemoryBasicDetails';
import MainInfoFormSkeleton from 'sharedComponents/Skeleton/MainInfoFormSkeleton';
import { useMemoryByID } from 'hooks/memory/useMemoryByID';
import { useUpdateMemory } from 'hooks/memory/useUpdateMemory';
import { getInitialStringValue } from 'utilities/form';

type MemoryEditSettingsProps = {
  memoryID: string;
  closeSideNav: () => void;
};

type SettingsFormType = {
  name: string;
  shortDescription: string;
  detailedDescription: string;
  imageURL: string;
};

function MemoryEditSettings({
  memoryID,
  closeSideNav,
}: MemoryEditSettingsProps) {
  const { joinedMemory, isLoading } = useMemoryByID(memoryID);

  const {
    mutateAsync: updateMemory,
    isLoading: isUpdatingMemory,
    isError: isUpdateMemoryError,
  } = useUpdateMemory(joinedMemory?.id);

  const initialValues = {
    imageURL: getInitialStringValue(joinedMemory?.attributes?.iconURL),
    name: joinedMemory?.attributes?.name || '',
    shortDescription: joinedMemory?.attributes?.description || '',
    detailedDescription: joinedMemory?.attributes?.overview || '',
  };

  const validationSchema = React.useMemo(() => {
    return yup.object({
      name: yup
        .string()
        .required('Should be between 10 and 50 characters')
        .min(3, 'Name must be at least 3 characters')
        .max(50),
      shortDescription: yup
        .string()
        .required('Should be between 50 and 150 characters')
        .min(50, 'Description must be at least 50 characters')
        .max(150),
      detailedDescription: yup
        .string()
        .required('Should be between 50 and 1000 characters')
        .min(50, 'Description must be at least 50 characters')
        .max(1000),
    });
  }, []);

  const onUpdateThumbnail = async (imageURL: string) => {
    await updateMemory({
      iconURL: imageURL,
    });
  };

  const handleSave = async (values: SettingsFormType) => {
    await updateMemory({
      iconURL: values.imageURL,
      name: values?.name,
      description: values?.shortDescription,
      overview: values?.detailedDescription,
    });
    if (!isUpdateMemoryError) {
      closeSideNav();
    }
  };

  if (isLoading) {
    return (
      <>
        <MainInfoFormSkeleton />
      </>
    );
  }

  return (
    <SparkForm<SettingsFormType>
      initialValues={initialValues}
      onSave={handleSave}
      validationSchema={validationSchema}
      containerProps={{
        height: '100%',
      }}
    >
      <Box width='100%' pb={2}>
        <MemoryThumbnail onUpdateThumbnail={onUpdateThumbnail} />
      </Box>
      <MemoryBasicDetails />
      <Box
        width={'100%'}
        display='flex'
        justifyContent='flex-end'
        alignItems='center'
        gap={2}
      >
        <SparkFormResetButton
          variant='outlined'
          sx={{ width: '200px', padding: '10px 20px', marginBottom: '10px' }}
        >
          Cancel
        </SparkFormResetButton>
        <SparkFormButton
          variant='contained'
          sx={{ width: '200px', padding: '10px 20px', marginBottom: '10px' }}
          disabled={isUpdatingMemory}
          shouldRequireFieldUpdates
        >
          Save
        </SparkFormButton>
      </Box>
    </SparkForm>
  );
}

export default MemoryEditSettings;
